/**
 * Sprint picker — top-bar dropdown. Lists sprints (planned/active/completed)
 * plus a "Backlog" option (sprintId = null).
 */

import { forwardRef, useEffect, useId, useRef, useState } from 'react'
import { useBoard } from '@/features/projects/board'
import { cn } from '@/lib/utils'

interface SprintPickerProps {
  wsId: string
  pId: string
  value: string | null | 'all'
  onChange: (sprintId: string | null | 'all') => void
}

export function SprintPicker({ wsId, pId, value, onChange }: SprintPickerProps) {
  const board = useBoard(wsId, pId)
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const firstItemRef = useRef<HTMLButtonElement>(null)
  const menuId = useId()
  const sprints = board.data?.sprints ?? []
  const active = sprints.find((s) => s.status === 'active')

  const close = (restoreFocus = false) => {
    setOpen(false)
    if (restoreFocus) requestAnimationFrame(() => triggerRef.current?.focus())
  }

  useEffect(() => {
    if (!open) return

    const onPointerDown = (event: PointerEvent) => {
      if (event.target instanceof Node && !rootRef.current?.contains(event.target)) close()
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        close(true)
      }
    }

    firstItemRef.current?.focus()
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const label =
    value === 'all' ? 'All sprints' :
    value === null ? 'Backlog' :
    sprints.find((s) => s.id === value)?.name ?? 'Pick sprint'

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((current) => !current)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-controls={open ? menuId : undefined}
        aria-label="Choose sprint"
        className="flex items-center gap-1.5 rounded-md border border-surface-300 dark:border-surface-700 bg-white dark:bg-surface-800 px-2.5 py-1 text-xs font-medium hover:border-brand-500"
      >
        <span className="h-1.5 w-1.5 rounded-full" style={{ backgroundColor: active ? '#22c55e' : '#a0a0ab' }} />
        {label}
        <span className="text-[10px] text-surface-500">▾</span>
      </button>
      {open && (
        <div id={menuId} role="menu" className="absolute z-10 mt-1 w-56 rounded-md border border-surface-200 dark:border-surface-700 bg-white dark:bg-surface-800 py-1 shadow-lg">
          <Item
            ref={firstItemRef}
            label="All sprints"
            onClick={() => { onChange('all'); close(true) }}
            selected={value === 'all'}
          />
          <Item
            label="Backlog"
            sub="No sprint"
            onClick={() => { onChange(null); close(true) }}
            selected={value === null}
          />
          <div className="my-1 border-t border-surface-200 dark:border-surface-700" />
          {sprints.length === 0 && (
            <div className="px-3 py-2 text-xs text-surface-500">No sprints yet.</div>
          )}
          {sprints
            .slice()
            .sort((a, b) => (a.status === 'active' ? -1 : b.status === 'active' ? 1 : 0))
            .map((s) => (
              <Item
                key={s.id}
                label={s.name}
                sub={s.status}
                onClick={() => { onChange(s.id); close(true) }}
                selected={value === s.id}
              />
            ))}
        </div>
      )}
    </div>
  )
}

const Item = forwardRef<HTMLButtonElement, {
  label: string
  sub?: string
  onClick: () => void
  selected: boolean
}>(({ label, sub, onClick, selected }, ref) => {
  return (
    <button
      ref={ref}
      type="button"
      role="menuitemradio"
      aria-checked={selected}
      onClick={onClick}
      className={cn(
        'flex w-full items-center justify-between gap-2 px-3 py-1.5 text-left text-xs hover:bg-surface-100 dark:hover:bg-surface-700',
        selected && 'bg-brand-500/10 text-brand-700 dark:text-brand-200',
      )}
    >
      <span>{label}</span>
      {sub && <span className="text-[10px] text-surface-500">{sub}</span>}
    </button>
  )
})
Item.displayName = 'Item'
